import React from "react";
import { I18n } from "react-redux-i18n";
import { Modal, ModalBody, ModalHeader, ModalTitle } from '@common/components/modal';

import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";
import { Flex } from "@common/components/Flex";
import { Loading } from "@common/components/loading";
import { HttpErrorDto } from "@common/dto/http";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import Tree, { TreeGetIdArgs } from "@common/components/Tree";
import { MODULE_NAME } from "@modules/pedigrees/constants/shared";

import "./move-pedigree.styles.scss";

export type MovePedigreeProps = {
  onClose: () => void;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  pedigreeName: string
  showErrorMessage: (error: string) => void;
  onSubmit: (folder: FolderPrimaryListDto) => Promise<void>;
};

export const MovePedigree: React.FC<MovePedigreeProps> = (props) => {
  const { onClose, onSubmit, showErrorMessage, getFolders, pedigreeName } = props;
  const [submitting, setSubmitting] = React.useState(false);
  const [initializing, setInitializingState] = React.useState(true);
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | undefined>();

  React.useEffect(() => {
    setInitializingState(true);
    getFolders()
      .then((data) => {
        setFolders(data);
      })
      .catch((err) => {
        showErrorMessage(err?.entity || I18n.t("Failed to get folders"));
        console.error(err);
      })
      .finally(() => {
        setInitializingState(false);
      });
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!selectedFolder) {
      return;
    }
    
    setSubmitting(true);

    try {
      await onSubmit(selectedFolder);
    } catch (err) {
      console.error(err);
      setSubmitting(false);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t("Failed to move pedigree"));
    }
  }, [onSubmit, showErrorMessage, selectedFolder]);

  const handleFolderChange = React.useCallback((id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    if (id === selectedFolder?.id) {
      return;
    }

    setSelectedFolder(folder);
  }, [selectedFolder]);

  const getFolderId = React.useMemo(() => {
    return {
      folder: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}`,
      collapseIcon: (params: TreeGetIdArgs) => `tree-root-child${params.parentIndex || 0}-child${params.ownIndex}-image`,
    };
  }, []);

  return (
    <Modal
      show
      animation={false}
      bsSize="medium"
      backdrop="static"
      bsClass="bootstrap modal"
      className="PedigreesMovePedigree"
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle
          {...getTestAttributes(MODULE_NAME, 'move-pedigree', 'modal-title')}
        >
          {I18n.t("Move Pedigree")}
        </ModalTitle>
      </ModalHeader>

      <ModalBody>
        {initializing 
          ? <Flex className="PedigreesMovePedigree__loading" justify="center" alignItems="center"><Loading size={30} /></Flex>
          : (
            <>
              <Flex alignItems="center" justify="space-between">
                <span>{I18n.t("Pedigree Name")}: {pedigreeName}</span>
                <Button
                  disabled={submitting || !selectedFolder || initializing}
                  {...getTestAttributes({ id: 'btn-move-ped-save' })}
                  onClick={handleSubmit}
                >
                  {I18n.t("Move")}
                </Button>
              </Flex>
              <Tree 
                getId={{
                  folder: getFolderId.folder,
                  collapseIcon: getFolderId.collapseIcon,
                }}
                className="PedigreesMovePedigree__folders"
                items={folders}
                value={selectedFolder ? selectedFolder.id : undefined} 
                onChange={handleFolderChange} 
              />
            </>
          )
        }
      </ModalBody>
    </Modal>
  );
};
